import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { BellRing, Loader2, Play, Send, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});
const PRI = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600' };

const IntelligentNotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [ctx, setCtx] = useState('{\n  "user_id": "REPLACE_WITH_USER_ID",\n  "document_expiring_days": 5,\n  "compliance_lost": true\n}');
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/intelligent-notifications/triggers`, { headers: authHeaders() });
        if (res.status === 403) { setAvailable(false); return; }
        setAvailable(res.ok);
      } catch { /* keep */ } finally { setLoading(false); }
    })();
  }, []);

  const parseCtx = () => { try { return JSON.parse(ctx); } catch { setMsg('Context must be valid JSON.'); return null; } };

  const suggest = async () => {
    setMsg(''); setResult(null);
    const parsed = parseCtx(); if (!parsed) return;
    const res = await fetch(`${API_URL}/api/intelligent-notifications/suggest`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ context: parsed }),
    });
    if (!res.ok) { setMsg('Suggest failed'); return; }
    setResult(await res.json());
  };

  const dispatch = async (s) => {
    setMsg('');
    const parsed = parseCtx(); if (!parsed) return;
    const res = await fetch(`${API_URL}/api/intelligent-notifications/dispatch`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ user_id: s.user_id, trigger: s.trigger, data: parsed }),
    });
    if (res.status === 403) { setMsg('Dispatch requires Super Admin (human oversight for high-impact sends).'); return; }
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.detail || 'Dispatch failed'); return; }
    const d = await res.json();
    setMsg(`Dispatched "${d.trigger}" via notification service. Delivery: ${JSON.stringify(d.delivery_results)}`);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="intelligent-notifications-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BellRing className="w-7 h-7 text-cyan-600" /> Intelligent Notifications
          </h1>
          <p className="text-gray-600 mt-1">
            Get explainable notification suggestions from operational signals. Every send is human-approved and routed through the central notification service.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="intelligent-notifications-loading"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="intelligent-notifications-unavailable">
            <p className="text-gray-700">Intelligent Notifications is not currently available.</p>
          </div>
        ) : (
          <>
            {msg && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 break-words" data-testid="intelligent-notifications-msg">{msg}</div>}

            <div className="flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-cyan-800 text-sm" data-testid="intelligent-notifications-banner">
              <Info className="w-5 h-5" /> Suggestions are advisory. Delivery only happens on explicit human dispatch, through the central notification service.
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="intelligent-notifications-suggest-panel">
              <h2 className="font-semibold text-gray-900">Operational Context</h2>
              <textarea value={ctx} onChange={(e) => setCtx(e.target.value)} rows={5} data-testid="in-context-input"
                className="border rounded-lg px-3 py-2 text-sm w-full font-mono" />
              <button onClick={suggest} data-testid="in-suggest-btn"
                className="inline-flex items-center gap-2 bg-cyan-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cyan-700 transition-colors">
                <Play className="w-4 h-4" /> Suggest
              </button>
            </div>

            {result && (
              <div className="space-y-2" data-testid="intelligent-notifications-suggestions">
                <p className="text-xs text-gray-500">{result.suggestions.length} suggestion(s) · AI considered: {String(result.ai_considered)}</p>
                {result.suggestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-400" data-testid="in-empty">No suggestions for this context.</div>
                ) : result.suggestions.map((s, i) => (
                  <div key={i} data-testid={`in-suggestion-${i}`} className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{s.trigger}</span>
                          <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${PRI[s.priority]}`}>{s.priority}</span>
                          {s.critical && <span className="text-[10px] uppercase text-red-500">critical</span>}
                          <span className="text-[10px] uppercase text-gray-400">{s.source}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">channels: {(s.channels_preview || []).join(', ')} · user: {s.user_id || '—'}</p>
                        <details className="mt-1"><summary className="text-xs text-cyan-600 cursor-pointer">Why?</summary>
                          <pre className="text-[11px] text-gray-600 whitespace-pre-wrap mt-1">{JSON.stringify(s.reasoning, null, 2)}</pre>
                        </details>
                      </div>
                      <button onClick={() => dispatch(s)} data-testid={`in-dispatch-${i}`}
                        className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:underline whitespace-nowrap">
                        <Send className="w-4 h-4" /> Dispatch
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default IntelligentNotificationsPage;
